import type { ThreatMapMeta } from '@/types/map'

export interface StatusBarProps {
  meta: ThreatMapMeta
  analyst: string
  shift: string
  priority: string
  feedAgeMs: number
}

/**
 * SOC-style status bar for the threat map.
 *
 * Left cluster: connection-state dot + ingest rate + attribution/MITRE versions.
 * Right cluster: analyst identity + shift + priority (sourced by caller from
 * the ZITADEL session on the MapNextPage).
 *
 * Connection-state dot:
 * - `feedAgeMs < 90s`  → green dot (`data-state="ok"`), label "FEED OK"
 * - `feedAgeMs >= 90s` → red pulsing dot (`data-state="stale"`),
 *   label "LAST FEED {n} m AGO" using floor(feedAgeMs / 60s).
 *
 * SOC-mode visibility is managed by the parent (`MapNextPage` omits the
 * StatusBar entirely when `socMode` is true) — this component itself is
 * always visible when rendered.
 */
export function StatusBar({
  meta,
  analyst,
  shift,
  priority,
  feedAgeMs,
}: StatusBarProps) {
  const stale = feedAgeMs >= 90_000
  const mins = Math.floor(feedAgeMs / 60_000)

  return (
    <footer
      data-testid="map-status-bar"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--text-tertiary)]"
    >
      <div className="flex items-center gap-3">
        <span
          className="flex items-center gap-1.5"
          style={{
            color: stale ? 'var(--severity-high)' : 'var(--status-active)',
          }}
        >
          <span
            data-testid="status-dot"
            data-state={stale ? 'stale' : 'ok'}
            className={`h-2 w-2 rounded-full ${stale ? 'animate-pulse' : ''}`}
            style={{
              background: stale ? 'var(--severity-high)' : 'var(--status-active)',
            }}
          />
          {stale ? `LAST FEED ${mins} m AGO` : 'FEED OK'}
        </span>
        <Sep />
        <span>INGEST {meta.ingestionRatePerSec.toFixed(1)} Evt/s</span>
        <Sep />
        <span>ATTRIB ENGINE {meta.attribVersion}</span>
        <Sep />
        <span>MITRE {meta.mitreVersion}</span>
      </div>
      <div className="flex items-center gap-3 text-[color:var(--text-secondary)]">
        <span>ANALYST: {analyst}</span>
        <Sep />
        <span>SHIFT {shift}</span>
        <Sep />
        <span
          className="rounded px-1.5 py-px"
          style={{
            color: 'var(--severity-high)',
            backgroundColor: 'color-mix(in oklch, var(--severity-high) 12%, transparent)',
          }}
        >
          PRIORITY {priority}
        </span>
      </div>
    </footer>
  )
}

function Sep() {
  return (
    <span
      className="inline-block h-3 w-px shrink-0 bg-[color:var(--border-subtle)]"
      aria-hidden
    />
  )
}
